import React from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { PlatformsFilter } from '../FormFilters/Platforms/PlatformsFilter';
import { ContentTypesFilter } from '../FormFilters/ContentTypes/ContentTypesFilter';
import { CollectCommentsSubgroup } from './CollectCommentsSubgroup';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { canCollectComments } from '../../utils/contentTypes';
import { ExcludeSwitch } from '../../pages/SearchPage/Filter/ExcludeSwitch';
import { languageOptions } from '../../utils/languages';
import type { NarrativeFormData } from '../Forms/types';
import styles from '../../pages/NarrativeAddPage/NarrativeAddPage.module.scss';

interface PlatformsContentGroupProps {
  form: any;
  isDisabled?: boolean;
}

export const PlatformsContentGroup: React.FC<PlatformsContentGroupProps> = ({ form, isDisabled = false }) => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();

  // Backend ignores the per-post limit / sample percent without this workspace flag.
  const featureFlags: string[] = currentUser?.workspace?.config?.featureflags || [];
  const showCollectCommentsSettings =
    featureFlags.includes('collect_comments_to_posts') && featureFlags.includes('collect_comments_experimental');

  const languageSelectOptions = languageOptions.map((option) => ({
    value: option.value,
    label: t(option.label),
  }));

  return (
    <>
      <div className="form-group-header">
        <span>{t('Platforms and Content')}</span>
      </div>

      <div className="group-card">
        <form.Field
          name="sourceTypes"
          children={(field: any) => (
            <PlatformsFilter
              selected={field.state.value || []}
              onChange={(selected) => field.handleChange(selected)}
              error={field.state.meta.errors?.[0]}
              isDisabled={isDisabled}
            />
          )}
        />

        <form.Subscribe
          selector={(state: any) => ({
            sourceTypes: state.values.sourceTypes,
          })}
          children={(sourceTypes: any) => {
            return (<form.Field
              name="collectCommentsToPosts"
              children={(collectCommentsField: any) => (
                <form.Field
                  name="contentTypes"
                  children={(field: any) => (
                    <ContentTypesFilter
                      selected={field.state.value || []}
                      selectedPlatforms={sourceTypes?.sourceTypes}
                      onChange={(selected, collectComments) => {
                        field.handleChange(selected);
                        collectCommentsField.handleChange(collectComments);
                      }}
                      collectComments={collectCommentsField.state.value || false}
                      error={field.state.meta.errors?.[0]}
                      isDisabled={isDisabled}
                    />
                  )}
                />
              )}
            />)
          }}
        />

        {showCollectCommentsSettings && (
          <form.Subscribe
            selector={(state: any) =>
              Boolean(state.values.collectCommentsToPosts) &&
              canCollectComments(state.values.contentTypes, state.values.sourceTypes)
            }
            children={(isCollectingComments: boolean) =>
              isCollectingComments && <CollectCommentsSubgroup form={form} isDisabled={isDisabled} />
            }
          />
        )}

        <div className={'form-element'}>
          <div className='labelContainer'>
            <label className='label'>
              {t('Content languages')}
            </label>
            <form.Field
              name="languagesExclude"
              children={(field: any) => (
                <ExcludeSwitch
                  id="language-switch"
                  value={field.state.value || false}
                  onChange={(value:any) => field.handleChange(value)}
                  color="red"
                  direction={'row-reverse'}
                  isDisabled={isDisabled}
                />
              )}
            />
          </div>
          <form.Field
            name="languages"
            children={(field: any) => (
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={languageSelectOptions}
                value={field.state.value || []}
                onChange={(value) => field.handleChange(value)}
                onBlur={field.handleBlur}
                placeholder={t('Select languages')}
                isMulti
                isClearable
                isSearchable
                isDisabled={isDisabled}
              />
            )}
          />
        </div>
      </div>
    </>
  );
};